import { useState, useRef } from "react";
import Header from "../components/common/Header";
import Text from "../components/common/Text";
import SectionHeading from "../components/common/SectionHeading";
import WrappedButton from "../components/common/Button";
import { Link } from "react-router-dom";
import { Field, Input, Label, Textarea, Checkbox } from '@headlessui/react';
import CheckCircle from "@untitled-ui/icons-react/build/cjs/CheckCircle";
import Users03 from "@untitled-ui/icons-react/build/cjs/Users03";
import LineChartDown03 from "@untitled-ui/icons-react/build/cjs/LineChartDown03";
import LineChartUp02 from "@untitled-ui/icons-react/build/cjs/LineChartUp02";
import PlusCircle from "@untitled-ui/icons-react/build/cjs/PlusCircle";
import MinusCircle from "@untitled-ui/icons-react/build/cjs/MinusCircle";

const KrankenkassenNavbar = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navItems = [
        { label: "Anforderungen", href: "#jobs-section" },
        { label: "Lösung", href: "#solution-section" },
        { label: "Pilot", href: "#pilot-section" },
        { label: "Einsatzszenarien", href: "#usecases-section" }
    ];

    const handleNavClick = (e, href) => {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
        setMobileMenuOpen(false);
    };

    return (
        <nav className="fixed w-full pt-[18px] pb-3 md:pb-[18px] top-0 z-20 bg-white shadow-sm">
            <div className="container hidden md:flex justify-between items-center">
                <div className="flex items-center">
                    <Link to="/">
                        <img loading="lazy" src="/img/docjo-icon.svg" className="cursor-pointer w-[103px] h-[43px]" alt="docjo" />
                    </Link>
                    <div className="flex ml-8 gap-1">
                        {navItems.map((item, index) => (
                            <a
                                key={index}
                                href={item.href}
                                onClick={(e) => handleNavClick(e, item.href)}
                                className="px-3 py-2 text-md font-semibold text-gray-700 hover:text-primary-500 transition-colors rounded-md hover:bg-gray-50"
                            >
                                {item.label}
                            </a>
                        ))}
                    </div>
                </div>
                <WrappedButton
                    className="link-button bg-primary-500 font-size-text-md"
                    onClick={(e) => handleNavClick(e, '#contact-section')}
                >
                    Gespräch anfragen
                </WrappedButton>
            </div>

            <div className="container flex md:hidden flex-col">
                <div className="flex justify-between items-center">
                    <Link to="/">
                        <img loading="lazy" src="/img/docjo-icon.svg" className="cursor-pointer w-[103px] h-[43px]" alt="docjo" />
                    </Link>
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="p-2 text-gray-500 rounded-lg"
                    >
                        {mobileMenuOpen ? (
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M6 18L18 6M6 6L18 18" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        ) : (
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 6H20M4 12H20M4 18H20" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </button>
                </div>
                {mobileMenuOpen && (
                    <div className="flex flex-col mt-4 pb-4 border-t border-gray-200 pt-4">
                        {navItems.map((item, index) => (
                            <a
                                key={index}
                                href={item.href}
                                onClick={(e) => handleNavClick(e, item.href)}
                                className="py-3 text-sm font-medium text-gray-700 hover:text-primary-500 border-b border-gray-100"
                            >
                                {item.label}
                            </a>
                        ))}
                        <WrappedButton
                            className="link-button bg-primary-500 font-size-text-md mt-4 w-full text-center"
                            onClick={(e) => handleNavClick(e, '#contact-section')}
                        >
                            Gespräch anfragen
                        </WrappedButton>
                    </div>
                )}
            </div>
        </nav>
    );
};

const HeroSection = () => {
    const badgeText = "CE-zertifiziert · White Label · DSGVO · messbar · Pilotfähig";
    const subBadges = ["Pilot in 6–12 Wochen", "Regulatorik & Datenschutz", "Einsatzszenarien"];

    return (
        <section className="container flex flex-col-reverse md:flex-row pt-[96px] md:pt-0">
            <section className="flex-1 pt-0 pb-7 md:pb-0 md:pt-4 self-center">
                <div className="breadcrumb-container flex min-w-fit w-min gap-2 pl-[12px] pr-[12px] pt-[6px] pb-[6px] mb-4">
                    <span className="text-textSecondary-700 font-medium text-sm">
                        {badgeText}
                    </span>
                </div>
                <Header className="font-size-display-xl line-height-display-xl header whitespace-pre-line mt-[20px] md:mt-2">
                    Digitale Versorgung für chronisch Erkrankte - einfach umsetzbar, voll integriert, klar messbar
                </Header>
                <Text className='font-size-text-xl mt-[20px] md:mt-3 font-normal line-height-text-x'>
                    docjo unterstützt Krankenkassen bei der strukturierten Begleitung chronisch erkrankter Versicherter – als CE-zertifizierte, White-Label-fähige Lösung mit transparenten KPIs.
                </Text>
                <div className='mt-4 md:mt-5 flex gap-3 flex-wrap'>
                    <WrappedButton className='font-size-text-md link-button bg-secondary-500 line-height-text-md'>
                        <Link to="#contact-section">
                            Unverbindliches Fachgespräch anfragen
                        </Link>
                    </WrappedButton>
                    {/* <WrappedButton className='font-size-text-md link-button bg-primary-500 line-height-text-md'>
                        <Link to="#contact-section">
                            Kurzkonzept anfordern
                        </Link>
                    </WrappedButton> */}
                </div>
                <div className="flex flex-wrap gap-4 mt-6 text-sm text-textSecondary-700">
                    {subBadges.map((item, index) => (
                        <span key={index} className="flex items-center gap-2">
                            <CheckCircle size={16} className="text-secondary-500" />
                            {item}
                        </span>
                    ))}
                </div>
            </section>
            <section className="flex-1 self-center flex justify-end">
                <img src="/img/header/header-background-de.png" className="self-end" alt="docjo App" />
            </section>
        </section>
    );
};

const ChallengeCard = ({ title, description }) => (
    <section className="flex-1 p-6 bg-white rounded-lg border border-gray-200">
        <Header variant="h3" className="font-semibold font-size-text-xl">{title}</Header>
        <Text className="font-size-text-md mt-2">{description}</Text>
    </section>
);

const ChallengesSection = () => {
    const challenges = [
        {
            title: "Geringe Kontinuität",
            description: "Punktuelle Arztkontakte bieten wenig Unterstützung zwischen den Terminen – kritische Phasen im Alltag bleiben unbegleitet."
        },
        {
            title: "Sinkende Adhärenz",
            description: "Im Alltag sinkt die Therapietreue, Symptome werden spät erkannt, Verschlechterungen oft erst im nächsten Termin thematisiert."
        },
        {
            title: "Fehlende Messbarkeit",
            description: "Die Wirkung bestehender Maßnahmen ist oft schwer messbar – KPIs fehlen, Transparenz über Versorgungsqualität bleibt eingeschränkt."
        },
        {
            title: "Isolierte Angebote",
            description: "Digitale Lösungen sind nicht konsequent in die Versorgung eingebettet und werden von Versicherten und Behandlern nicht akzeptiert."
        }
    ];

    return (
        <section className="pt-7 pb-7 md:pt-9 md:pb-9 gray-section" id="challenges-section">
            <div className="container">
                <Header variant="h2" className="font-size-display-md font-semibold line-height-display-md text-center">Warum das relevant ist</Header>
                <Text className="font-size-text-lg mt-4 text-center max-w-5xl mx-auto">
                    Chronische Erkrankungen benötigen Kontinuität im Alltag. Die Herausforderung liegt darin, dass zwischen den punktuellen Arztkontakten oft große Lücken in der Betreuung entstehen. Gerade in dieser Zeit – im Alltag der Versicherten – zeigt sich, ob Therapien wirken und ob die Adhärenz aufrechterhalten werden kann.
                </Text>
                <Text className="font-size-text-lg mt-4 text-center max-w-5xl mx-auto">
                    Digitale Versorgung kann diese Lücke schließen – wenn sie medizinisch fundiert, in bestehende Versorgungsstrukturen integriert und in ihrer Wirkung messbar ist. Nur so lässt sich nachweisen, dass digitale Angebote echten Mehrwert schaffen und nicht nur weitere isolierte Tools darstellen.
                </Text>
                <Header variant="h3" className="font-size-display-sm font-semibold line-height-display-sm text-center mt-12">Typische Herausforderungen</Header>
                <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                    {challenges.map((item, index) => (
                        <ChallengeCard key={index} {...item} />
                    ))}
                </section>
                <Text className="text-center mt-8 font-size-text-lg max-w-4xl mx-auto">
                    Es braucht eine digitale Ergänzung der Versorgung, die wirksam, akzeptiert und steuerbar ist – und sich pragmatisch pilotieren lässt.
                </Text>
            </div>
        </section>
    );
};

const JobCard = ({ icon, title, description }) => (
    <div className="p-6 bg-white rounded-lg border border-gray-200">
        <div className="flex justify-center mb-4">
            <div className="p-[12px] flex justify-center items-center solution-icon-wrapper w-[50px] h-[50px]">
                {icon}
            </div>
        </div>
        <Header variant="h4" className="font-semibold font-size-text-lg">{title}</Header>
        <Text className="font-size-text-md mt-2">{description}</Text>
    </div>
);

const JobCardSimple = ({ title, description }) => (
    <div className="p-6 bg-gray-50 rounded-lg">
        <Header variant="h4" className="font-semibold font-size-text-lg">{title}</Header>
        <Text className="font-size-text-md mt-2">{description}</Text>
    </div>
);

const JobsToBeDoneSection = () => {
    const jobsTop = [
        {
            icon: <Users03 size={24} />,
            title: "Versorgung kontinuierlich begleiten",
            description: "Alltagstaugliche Unterstützung zwischen Terminen ermöglichen und Versicherte in kritischen Phasen nicht allein lassen."
        },
        {
            icon: <LineChartUp02 size={24} />,
            title: "Wirksamkeit sichtbar machen",
            description: "KPIs statt Blackbox-Angebote – nachvollziehbare Daten zur Versorgungsqualität und Therapieadhärenz erheben."
        },
        {
            icon: <LineChartDown03 size={24} />,
            title: "Kosten stabilisieren",
            description: "Adhärenz erhöhen, Eskalationen und teure Notfallbehandlungen durch frühzeitige Intervention reduzieren."
        }
    ];

    const jobsBottom = [
        {
            title: "Risikoarm starten",
            description: "Pilotfähige Lösungen mit schnellem Time-to-Value ermöglichen – ohne langwierige Entwicklungsprojekte oder hohe Vorabinvestitionen."
        },
        {
            title: "Mitgliederbindung stärken",
            description: "Relevante Leistungen im Alltag erlebbar machen und sich als innovativer, versorgungsorientierter Partner positionieren."
        },
        {
            title: "Akzeptanz sichern",
            description: "Praktikabel für Versicherte und Behandler – nur so werden digitale Angebote tatsächlich genutzt und integriert."
        }
    ];

    return (
        <section className="pt-7 pb-7 md:pt-9 md:pb-9" id="jobs-section">
            <div className="container">
                <SectionHeading
                    upperHeader="Jobs-to-be-Done"
                    header="Was Krankenkassen heute leisten müssen"
                    subHeader="Die Anforderungen an moderne Krankenkassen gehen weit über die reine Kostenerstattung hinaus. Es gilt, Versorgungsqualität aktiv zu gestalten, Wirksamkeit transparent zu machen und gleichzeitig wirtschaftlich zu agieren."
                />
                <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-[64px]">
                    {jobsTop.map((item, index) => (
                        <JobCard key={index} {...item} />
                    ))}
                </section>
                <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                    {jobsBottom.map((item, index) => (
                        <JobCardSimple key={index} {...item} />
                    ))}
                </section>
            </div>
        </section>
    );
};

const SolutionFeatureSimple = ({ title, features }) => (
    <div className="flex-1">
        <Header variant="h3" className="font-semibold font-size-text-xl mb-4">{title}</Header>
        <ul className="space-y-3">
            {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                    <span className="text-gray-600 mt-1">•</span>
                    <Text className="font-size-text-md">{feature}</Text>
                </li>
            ))}
        </ul>
    </div>
);

const SolutionSection = () => {
    const solutions = [
        {
            title: "Medizinische App",
            features: [
                "Strukturierte Begleitung im Alltag mit evidenzbasierten Inhalten",
                "Symptomabfragen & Selbstmanagement-Tools für chronisch Erkrankte",
                "Niedrigschwellige Nutzung ohne technische Hürden"
            ]
        },
        {
            title: "Hybridversorgung",
            features: [
                "Ergänzt ärztliche Behandlung, ersetzt sie nicht",
                "Kontinuität zwischen Terminen durch digitale Begleitung",
                "Unterstützt Therapieeffizienz durch strukturierte Daten"
            ]
        },
        {
            title: "Dashboard & Reporting",
            features: [
                "Aggregierte Auswertungen auf Kohorten-Ebene",
                "KPIs: Adhärenz, Symptomverlauf, Engagement-Raten",
                "Steuerbarkeit & Transparenz für fundierte Entscheidungen"
            ]
        }
    ];

    return (
        <section className="pt-7 pb-7 md:pt-9 md:pb-9 gray-section" id="solution-section">
            <div className="container">
                <SectionHeading
                    upperHeader="Die Lösung"
                    header="docjo als integrierbare Versorgungslösung"
                    subHeader="docjo kombiniert eine medizinische App für Versicherte, hybride Versorgungselemente zur Einbindung von Behandlern und ein Dashboard für die Krankenkasse – optional vollständig als White Label im Branding Ihrer Organisation. Die Lösung ist modular aufgebaut und lässt sich flexibel an Ihre Versorgungsstrategie anpassen."
                />
                <section className="flex flex-col md:flex-row gap-8 mt-[64px]">
                    {solutions.map((item, index) => (
                        <SolutionFeatureSimple key={index} {...item} />
                    ))}
                </section>
                <div className="mt-8 p-6 bg-white rounded-lg">
                    <Text className="font-size-text-lg">
                        <strong>White Label:</strong> docjo kann vollständig im Branding Ihrer Krankenkasse ausgerollt werden – ohne Entwicklungsaufwand, mit klarer Leistungszuordnung und nahtloser Integration in Ihre bestehende Kommunikation.
                    </Text>
                </div>
            </div>
        </section>
    );
};

const BenefitCard = ({ title, benefits }) => (
    <div className="flex-1">
        <Header variant="h3" className="font-semibold font-size-text-xl mb-4">{title}</Header>
        <ul className="space-y-3">
            {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2">
                    <span className="text-gray-600 mt-1">•</span>
                    <Text className="font-size-text-md">{benefit}</Text>
                </li>
            ))}
        </ul>
    </div>
);

const BenefitsSection = () => {
    const benefitGroups = [
        {
            title: "Für die Krankenkasse",
            benefits: [
                "Messbare Versorgungsergebnisse durch transparente KPIs",
                "Geringer Implementierungsaufwand dank White-Label-Lösung",
                "Differenzierung im Markt durch innovatives Versorgungsprofil"
            ]
        },
        {
            title: "Für Versicherte",
            benefits: [
                "Kontinuierliche Begleitung im Alltag bei chronischer Erkrankung",
                "Mehr Sicherheit durch strukturierte Symptomkontrolle",
                "Einfacher, niedrigschwelliger Zugang ohne technische Barrieren"
            ]
        },
        {
            title: "Für Behandler",
            benefits: [
                "Strukturierte Informationen zum Krankheitsverlauf zwischen Terminen",
                "Entlastung im Praxisalltag durch bessere Vorbereitung",
                "Unterstützung statt Bürokratie – kein zusätzlicher Dokumentationsaufwand"
            ]
        }
    ];

    return (
        <section className="pt-7 pb-7 md:pt-9 md:pb-9" id="benefits-section">
            <div className="container">
                <SectionHeading
                    upperHeader="Mehrwert"
                    header="Mehrwert entlang der Versorgungskette"
                    subHeader="Eine erfolgreiche digitale Versorgungslösung schafft Mehrwert für alle Beteiligten. Nur wenn Krankenkasse, Versicherte und Behandler profitieren, entsteht nachhaltige Akzeptanz und messbare Wirkung in der Versorgungsrealität."
                />
                <section className="flex flex-col md:flex-row gap-12 mt-[48px]">
                    {benefitGroups.map((item, index) => (
                        <BenefitCard key={index} {...item} />
                    ))}
                </section>
            </div>
        </section>
    );
};

const PilotStepLeft = ({ number, title, description }) => (
    <div className="flex items-start">
        <div className="flex-1 text-right pr-6">
            <Header variant="h4" className="font-semibold font-size-text-lg">{title}</Header>
            <Text className="font-size-text-md mt-1">{description}</Text>
        </div>
        <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center font-semibold flex-shrink-0">
            {number}
        </div>
        <div className="flex-1"></div>
    </div>
);

const PilotStepRight = ({ number, title, description }) => (
    <div className="flex items-start">
        <div className="flex-1"></div>
        <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center font-semibold flex-shrink-0">
            {number}
        </div>
        <div className="flex-1 pl-6">
            <Header variant="h4" className="font-semibold font-size-text-lg">{title}</Header>
            <Text className="font-size-text-md mt-1">{description}</Text>
        </div>
    </div>
);

const KpiCard = ({ title, description }) => (
    <div className="p-6 bg-white rounded-lg border border-gray-200">
        <Header variant="h4" className="font-semibold font-size-text-lg mb-2">{title}</Header>
        <Text className="font-size-text-md">{description}</Text>
    </div>
);

const PilotSection = () => {
    const sectionId = "pilot-section";
    const steps = [
        { number: 1, title: "Zielgruppe & Indikation", description: "Definition der Pilotkohorte abgestimmt auf Ihre Versorgungsstrategie – z.B. Asthma, COPD oder andere chronische Indikationen.", side: "left" },
        { number: 2, title: "KPI-Set & Evaluation", description: "Festlegung messbarer Erfolgsparameter: Adhärenz, Symptomverlauf, Engagement, Feedback – als Basis für die Auswertung.", side: "right" },
        { number: 3, title: "Branding & Setup", description: "White-Label-Anpassung optional, Einrichtung der Dashboard-Zugänge und technische Vorbereitung der Schnittstellen.", side: "left" },
        { number: 4, title: "Onboarding", description: "Versicherte und Behandler werden eingeschrieben, Startkommunikation erfolgt, Support-Strukturen werden aktiviert.", side: "right" },
        { number: 5, title: "Laufzeit & Auswertung", description: "Regelmäßige KPI-Reports während der Pilotphase, abschließender Evaluationsbericht mit Handlungsempfehlungen.", side: "left" }
    ];

    const kpis = [
        { title: "Adhärenz & Nutzung", description: "Kontinuität der App-Nutzung, Interaktionsraten, Aktivitätsmuster über den Pilotzeitraum hinweg." },
        { title: "Symptomverlauf", description: "Messung validierter Scores wie ACT (Asthma Control Test) zur Beurteilung der Krankheitskontrolle." },
        { title: "Engagement-Raten", description: "Nutzungstiefe, Feature-Akzeptanz, Abschlussraten bei geführten Programmen und Modulen." },
        { title: "Feedback", description: "Qualitatives Feedback von Versicherten und Behandlern zur Praktikabilität und wahrgenommenen Wirksamkeit." }
    ];

    return (
        <section className="pt-7 pb-7 md:pt-9 md:pb-9" id="pilot-section">
            <div className="container">
                <Header variant="h2" className="font-size-display-md font-semibold line-height-display-md">Pragmatischer Einstieg: Pilot in 6–12 Wochen</Header>
                <Text className="font-size-text-lg mt-4 max-w-5xl">
                    Ein Pilot bietet die Möglichkeit, docjo in einem kontrollierten Rahmen zu testen und in kurzer Zeit belastbare Erkenntnisse zu Nutzung, Akzeptanz und Versorgungseffekten zu gewinnen. Dieser Ansatz reduziert Einführungsrisiken erheblich und ermöglicht datenbasierte Entscheidungen über eine breitere Einführung.
                </Text>
                <Text className="font-size-text-lg mt-4 max-w-5xl">
                    Der Ablauf ist strukturiert und klar definiert, sodass Sie zu jedem Zeitpunkt volle Transparenz über den Projektfortschritt haben. Gemeinsam definieren wir Erfolgskriterien und KPIs, die für Ihre Versorgungsstrategie relevant sind.
                </Text>
                <div className="hidden md:block mt-[48px] space-y-6 max-w-4xl mx-auto">
                    {steps.map((step) => (
                        step.side === "left"
                            ? <PilotStepLeft key={step.number} {...step} />
                            : <PilotStepRight key={step.number} {...step} />
                    ))}
                </div>
                <div className="md:hidden mt-[48px] space-y-6">
                    {steps.map((step) => (
                        <div key={step.number} className="flex gap-4 items-start">
                            <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center font-semibold flex-shrink-0">
                                {step.number}
                            </div>
                            <div>
                                <Header variant="h4" className="font-semibold font-size-text-lg">{step.title}</Header>
                                <Text className="font-size-text-md mt-1">{step.description}</Text>
                            </div>
                        </div>
                    ))}
                </div>

                <Header variant="h3" className="font-size-display-sm font-semibold line-height-display-sm mt-12">Beispiel-KPIs für die Pilot-Evaluation</Header>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                    {kpis.map((kpi, index) => (
                        <KpiCard key={index} {...kpi} />
                    ))}
                </div>
            </div>
        </section>
    );
};

const RegulatorikSection = () => (
    <section className="pt-7 pb-7 md:pt-9 md:pb-9 gray-section" id="regulatorik-section">
        <div className="container">
            <Header variant="h2" className="font-size-display-md font-semibold line-height-display-md">
                Regulatorik & Datenschutz
            </Header>
            <div className="max-w-4xl">
                <Text className="font-size-text-lg mt-4">
                    docjo ist als Medizinprodukt CE-zertifiziert und erfüllt alle relevanten regulatorischen Anforderungen für den Einsatz im deutschen und europäischen Gesundheitsmarkt. Die Lösung wurde von Beginn an für den regulierten Kontext konzipiert und berücksichtigt die besonderen Anforderungen an Datenschutz und Informationssicherheit im Gesundheitswesen.
                </Text>
                <Text className="font-size-text-lg mt-4">
                    Die Verarbeitung personenbezogener Daten erfolgt DSGVO-konform. Technische und organisatorische Maßnahmen (TOMs) sind implementiert und dokumentiert. Auf Wunsch stellen wir Ihnen gerne alle relevanten Unterlagen zur Verfügung, einschließlich Datenschutz-Folgenabschätzungen (DPIA), Auftragsverarbeitungsverträgen (AVV) und detaillierten Sicherheitskonzepten.
                </Text>
                <Text className="font-size-text-lg mt-4">
                    Diese Grundlage schafft Vertrauen bei allen Stakeholdern und ermöglicht eine rechtssichere Integration in bestehende Versorgungsstrukturen.
                </Text>
            </div>
        </div>
    </section>
);

const UseCaseCard = ({ title, description }) => (
    <div className="flex-1 p-6 bg-gray-50 rounded-lg">
        <Header variant="h3" className="font-semibold font-size-text-xl mb-3">{title}</Header>
        <Text className="font-size-text-md">{description}</Text>
    </div>
);

const UseCasesSection = () => {
    const useCases = [
        {
            title: "Chronische Indikationen",
            description: "Fokus auf häufige chronische Erkrankungen wie Asthma, COPD oder Diabetes. Die Lösung kann zielgruppenbezogen gesteuert und auf spezifische Versorgungsbedarfe zugeschnitten werden. Evidenzbasierte Inhalte und validierte Assessments sichern medizinische Qualität."
        },
        {
            title: "Ergänzung bestehender Programme",
            description: "Integration in DMP-nahe Begleitstrukturen, Unterstützung beim Alltagstransfer von Therapieempfehlungen und Steigerung der Adhärenz durch kontinuierliche digitale Begleitung. docjo ergänzt bestehende Versorgungsangebote, ohne diese zu ersetzen."
        },
        {
            title: "Selektiv & Innovation",
            description: "Einsatz in Pilotierungen, regionalen Modellprojekten oder im Rahmen kooperativer Versorgungsformen (z.B. mit Ärztenetzwerken). Flexible Vertragsgestaltung ermöglicht innovative Ansätze jenseits der Regelversorgung."
        }
    ];

    return (
        <section className="pt-7 pb-7 md:pt-9 md:pb-9" id="usecases-section">
            <div className="container">
                <Header variant="h2" className="font-size-display-md font-semibold line-height-display-md">Einsatzszenarien</Header>
                <Text className="font-size-text-lg mt-4 max-w-5xl">
                    docjo ist flexibel einsetzbar und lässt sich an unterschiedliche Versorgungsszenarien anpassen. Ob als Ergänzung bestehender Programme, für spezifische Indikationen oder im Rahmen innovativer Selektivverträge – die modulare Architektur ermöglicht eine zielgerichtete Ausgestaltung.
                </Text>
                <section className="flex flex-col md:flex-row gap-6 mt-[48px]">
                    {useCases.map((item, index) => (
                        <UseCaseCard key={index} {...item} />
                    ))}
                </section>
            </div>
        </section>
    );
};

const SingleQuestion = ({ question, answer, showBorder }) => {
    const [isExpand, setIsExpand] = useState(false);
    return (
        <li className={`pt-3 pb-4 w-full ${showBorder ? "border-b border-solid border-borderFaq" : ""}`}>
            <div className="flex justify-between cursor-pointer" onClick={() => setIsExpand(!isExpand)}>
                <Header variant="h3" className='text-left font-size-text-lg font-medium line-height-text-lg'>{question}</Header>
                {isExpand ? <MinusCircle className="cursor-pointer" /> : <PlusCircle className="cursor-pointer" />}
            </div>
            <div className={`overflow-hidden ease-in-out ${isExpand ? 'max-h-96 opacity-100 duration-1000' : 'max-h-0 opacity-0 duration-300'}`}>
                <Text className='font-size-text-md line-height-text-md text-left mr-[32px] md:mr-[120px] mt-2'>
                    {answer}
                </Text>
            </div>
        </li>
    );
};

const NextStepsSection = () => {
    const steps = [
        {
            number: "01",
            title: "Erstgespräch vereinbaren",
            description: "Kurzer Austausch zu Ihren Versorgungszielen und ersten Fragen – unverbindlich und ohne Verpflichtung."
        },
        {
            number: "02",
            title: "Konzept & Unterlagen",
            description: "Sie erhalten ein individuelles Kurzkonzept sowie relevante Unterlagen zu Regulatorik, Datenschutz und Pilotierung."
        },
        {
            number: "03",
            title: "Entscheidung & Pilotplanung",
            description: "Gemeinsame Bewertung der Passung, bei Interesse Planung eines Pilotprojekts mit klaren KPIs und Zeitrahmen."
        }
    ];

    return (
        <section className="pt-7 pb-7 md:pt-9 md:pb-9 gray-section">
            <div className="container">
                <Header variant="h2" className="font-size-display-md font-semibold line-height-display-md">Nächste Schritte</Header>
                <Text className="font-size-text-lg mt-4 max-w-5xl">
                    Wir freuen uns auf den Austausch mit Ihnen. Nutzen Sie die Möglichkeit, docjo kennenzulernen und zu prüfen, ob unsere Lösung zu Ihren Anforderungen passt.
                </Text>

                <div className="flex flex-col md:flex-row gap-8 mt-8">
                    {steps.map((step, index) => (
                        <div key={index} className="flex-1 border-t-2 border-gray-300 pt-4">
                            <Text className="font-size-text-md text-gray-500 mb-2">{step.number}</Text>
                            <Header variant="h4" className="font-semibold font-size-text-lg mb-2">{step.title}</Header>
                            <Text className="font-size-text-md">{step.description}</Text>
                        </div>
                    ))}
                </div>

                {/* <div className='mt-8 flex gap-3 flex-wrap'>
                    <WrappedButton className='font-size-text-md link-button bg-primary-500 line-height-text-md'>
                        <a href="mailto:kontakt@docjo.com">
                            Jetzt Kontakt aufnehmen
                        </a>
                    </WrappedButton>
                    <WrappedButton className='font-size-text-md link-button bg-secondary-500 line-height-text-md'>
                        <a href="mailto:kontakt@docjo.com?subject=Informationsmaterial%20anfordern">
                            Informationsmaterial anfordern
                        </a>
                    </WrappedButton>
                </div> */}
            </div>
        </section>
    );
};

const ContactSection = () => {
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [acceptedTC, setAcceptedTC] = useState(false);
    const [tcMessage, setTcMessage] = useState("");
    const [emailMessage, setEmailMessage] = useState("");
    const [areaMessage, setAreaMessage] = useState("");
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const emailRef = useRef(null);

    const faqs = [
        {
            question: "Wie hoch ist der Implementierungsaufwand?",
            answer: "Gering. White-Label-Lösung, keine eigene Entwicklung nötig, technische Integration über Standard-Schnittstellen."
        },
        {
            question: "Wie schnell kann ein Pilot starten?",
            answer: "In der Regel innerhalb von 6–12 Wochen nach Vertragsabschluss, abhängig von interner Abstimmung."
        },
        {
            question: "Gibt es Unterlagen zu Datenschutz und Sicherheit?",
            answer: "Ja, wir stellen TOMs, DPIA, AVV und weitere Dokumente auf Anfrage bereit."
        }
    ];

    const validate = (email, message, tc) => {
        let bool = true;
        if (tc === false) {
            setTcMessage("Bitte akzeptieren Sie die Datenschutzerklärung.");
            bool = false;
        } else {
            setTcMessage("");
        }
        if (message.length < 1) {
            setAreaMessage("Bitte geben Sie eine Nachricht ein.");
            bool = false;
        } else {
            setAreaMessage("");
        }
        if (!String(email).toLowerCase().match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )) {
            bool = false;
            setEmailMessage("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
        } else {
            setEmailMessage("");
        }
        return bool;
    };

    const handleSendForm = (e) => {
        e.preventDefault();
        const valid = validate(email, message, acceptedTC);

        if (email.length > 0 && message.length > 0 && acceptedTC === true && valid === true) {
            const data = new FormData(e.target);
            fetch("https://formspree.io/f/mjggkvnv", {
                method: "POST",
                body: data,
                headers: {
                    'Accept': 'application/json'
                }
            })
                .then(res => {
                    if (res.ok) {
                        setEmail("");
                        setMessage("");
                        setAcceptedTC(false);
                        setShowSuccessModal(true);
                    }
                })
                .catch(error => console.log(error));
        }
    };

    return (
        <>
            {showSuccessModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg p-8 max-w-md mx-4 shadow-xl">
                        <div className="flex justify-center mb-4">
                            <div className="w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center">
                                <CheckCircle size={32} className="text-white" />
                            </div>
                        </div>
                        <Header variant="h3" className="font-semibold font-size-display-sm text-center mb-4">
                            Nachricht gesendet
                        </Header>
                        <Text className="font-size-text-md text-center text-gray-600 mb-6">
                            Vielen Dank für Ihre Nachricht. Wir haben Ihre Anfrage erhalten und werden uns schnellstmöglich bei Ihnen melden.
                        </Text>
                        <WrappedButton
                            variant="button"
                            className="w-full bg-primary-500 link-button font-size-text-md line-height-text-md"
                            onClick={() => setShowSuccessModal(false)}
                        >
                            Schließen
                        </WrappedButton>
                    </div>
                </div>
            )}
            <section className="pt-7 pb-7 md:pt-9 md:pb-9" id="contact-section">
                <div className="container">
                    <Header variant="h2" className="font-size-display-md font-semibold line-height-display-md">
                        Unverbindliches Fachgespräch
                    </Header>
                    <Text className="font-size-text-lg mt-4 max-w-5xl">
                        Wenn Sie prüfen möchten, ob docjo zu Ihrer Versorgungsstrategie passt, vereinbaren wir gerne einen kurzen, unverbindlichen Austausch. In einem Gespräch von 15–30 Minuten können wir Ihre spezifischen Anforderungen besprechen, offene Fragen klären und gemeinsam bewerten, ob eine Zusammenarbeit sinnvoll ist.
                    </Text>
                    <Text className="font-size-text-lg mt-4 max-w-5xl">
                        Wir legen Wert auf einen sachlichen, transparenten Dialog auf Augenhöhe – ohne Verkaufsdruck, dafür mit fundierten Informationen zu regulatorischen, technischen und versorgungsstrategischen Aspekten.
                    </Text>

                    <div className="flex flex-col md:flex-row gap-12 mt-8">
                        <div className="flex-1">
                            <Header variant="h3" className="font-semibold font-size-text-xl mb-4">Kontakt</Header>
                            <div className="space-y-2">
                                <Text className="font-size-text-md"><strong>Ansprechpartner:</strong> Roger Pierenkemper</Text>
                                <Text className="font-size-text-md"><strong>Rolle:</strong> Strategischer Berater Krankenkassen</Text>
                                <Text className="font-size-text-md"><strong>E-Mail:</strong> <a href="mailto:roger.pierenkemper@docjo.health" className="text-secondary-500">roger.pierenkemper@docjo.health</a></Text>
                                {/* <Text className="font-size-text-md"><strong>Telefon:</strong> [+49 / +41 ...]</Text> */}
                            </div>

                            <Header variant="h3" className="font-semibold font-size-text-xl mt-8 mb-4">Häufige Fragen</Header>
                            <ul className="space-y-4">
                                {faqs.map((faq, index) => (
                                    <li key={index}>
                                        <Text className="font-size-text-md"><strong>{faq.question}</strong> – {faq.answer}</Text>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="flex-1">
                            <form onSubmit={handleSendForm}>
                                <Header variant="h3" className="font-semibold font-size-text-xl mb-4">Nachricht senden</Header>
                                <Field className="mb-4">
                                    <Label><Text className="font-size-text-sm font-medium line-height-text-sm">E-Mail</Text></Label>
                                    <Input
                                        ref={emailRef}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Ihre E-Mail-Adresse"
                                        type="email"
                                        name="email"
                                        className={`mt-1 pt-[10px] pb-[10px] pl-[14px] pr-[14px] border w-full input-base ${emailMessage.length > 0 ? 'validated-input-error' : ''}`}
                                    />
                                    {emailMessage.length > 0 && <Text variant="span" className="text-red-400 text-sm">{emailMessage}</Text>}
                                </Field>

                                <Field className="mb-4">
                                    <Label><Text className="font-size-text-sm font-medium line-height-text-sm">Nachricht</Text></Label>
                                    <Textarea
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        rows={5}
                                        placeholder="Ihre Nachricht..."
                                        name="message"
                                        className={`mt-1 pt-[12px] pb-[12px] pl-[14px] pr-[14px] border w-full input-base resize-none ${areaMessage.length > 0 ? 'validated-input-error' : ''}`}
                                    />
                                    {areaMessage.length > 0 && <Text variant="span" className="text-red-400 text-sm">{areaMessage}</Text>}
                                </Field>

                                <Field className="mb-4 flex gap-x-2 items-start relative">
                                    <Checkbox
                                        checked={acceptedTC}
                                        onChange={setAcceptedTC}
                                        className="group block size-4 rounded border bg-white data-[checked]:bg-primary-500 data-[checked]:border-primary-500 w-[24px] h-[24px] self-center border-2 flex-shrink-0 transition-colors"
                                    >
                                        <svg className="stroke-white opacity-0 group-data-[checked]:opacity-100" viewBox="0 0 14 14" fill="none">
                                            <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </Checkbox>
                                    <Label>
                                        <Text className="text-sm">
                                            Ich akzeptiere die <Link to="/privacy-policy" target="_blank" className="font-semibold text-secondary-500">Datenschutzerklärung</Link>.
                                        </Text>
                                    </Label>
                                </Field>
                                {tcMessage.length > 0 && <Text variant="span" className="text-red-400 text-sm block mb-4">{tcMessage}</Text>}

                                <WrappedButton type="submit" variant="button" className="w-full bg-primary-500 link-button font-size-text-md line-height-text-md">
                                    Nachricht senden
                                </WrappedButton>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

const KrankenkassenLandingPage = () => {
    return (
        <section>
            <KrankenkassenNavbar />
            <HeroSection />
            <ChallengesSection />
            <JobsToBeDoneSection />
            <SolutionSection />
            <BenefitsSection />
            <PilotSection />
            <RegulatorikSection />
            <UseCasesSection />
            <NextStepsSection />
            <ContactSection />
        </section>
    );
};

export default KrankenkassenLandingPage;
